import _ from "lodash"
import { helperFunctions } from '../functions/HelperFunctions'
import { CommonResponses } from './CommonResponses'

interface BaseToken {
	line: number
	column: number
}

export const TokenType = {
	WHITESPACE: 'WHITESPACE',
	COMMENT: 'COMMENT',
	NUMBER: 'NUMBER',
	STRING: 'STRING',
	ARRAY: 'ARRAY',
	LOGICAL_OPERATOR: 'LOGICAL_OPERATOR',
	KEYWORD: 'KEYWORD',
	FUNCTION: 'FUNCTION',
	LITERAL: 'LITERAL',
	CONSTANT: 'CONSTANT',
	IDENTIFIER: 'IDENTIFIER',
	SPECIAL_IDENTIFIER: 'SPECIAL_IDENTIFIER',
	OPERATOR: 'OPERATOR',
	PUNCTUATION: 'PUNCTUATION',
	LINEBREAK: 'LINEBREAK',
	VALIDATION_KEYWORD: 'VALIDATION_KEYWORD',
	ACTIVATION_KEYWORD: 'ACTIVATION_KEYWORD',
} as const

export type TokenTypeValue = typeof TokenType[keyof typeof TokenType]
export type TokenPattern = { type: TokenTypeValue; pattern: RegExp }

// This is a special token that is not produced by the lexer
export const EOF_TOKEN_TYPE = 'EOF'

export interface ScalarToken extends BaseToken {
	type: Exclude<TokenTypeValue, typeof TokenType.ARRAY> | typeof EOF_TOKEN_TYPE
	value: string
}

export interface ArrayToken extends BaseToken {
	type: typeof TokenType.ARRAY
	value: Token[]
}

export type Token = ScalarToken | ArrayToken

export const ValidationKeywords = {
	VALID: 'VALID',
	INVALID: 'INVALID',
	ABNORMAL: 'ABNORMAL',
	OUT_OF_RANGE: 'OUT_OF_RANGE',
	REQUIRED: 'REQUIRED',
} as const

export const ActivationKeywords = {
	ACTIVE: 'ACTIVE',
	INACTIVE: 'INACTIVE',
	DISABLED: 'DISABLED',
	HIDDEN: 'HIDDEN',
} as const

export const Keywords = {
	LET: 'LET',
	CONCEPT: 'CONCEPT',
	RETURN: 'RETURN',
	PRINT: 'PRINT',
	NOT: 'NOT',
	SET: 'SET',
	IN: 'IN',
	CONTINUE: 'CONTINUE'
} as const

export const Literals = {
	TRUE: 'TRUE',
	FALSE: 'FALSE',
	NULL: 'NULL',
} as const

export const Constants = {
	MEMBER_AGE: 'MEMBER_AGE',
	MEMBER_NAME: 'MEMBER_NAME',
	MEMBER_GENDER: 'MEMBER_GENDER',
	DATE_OF_SERVICE: 'DATE_OF_SERVICE',
	CLINICIAN_NAME: 'CLINICIAN_NAME',
	IS_VIDEO_REQUIRED: 'IS_VIDEO_REQUIRED',
} as const

export const CoreFunctions = {
	IF: 'IF',
	ELSE: 'ELSE',
	FOR: 'FOR',
} as const

// only valid directly after RETURN; the parser expands each into IF/ELSE returns
export const ReturnHelpers = {
	ACTIVE_WHEN: 'ACTIVE_WHEN',
	REQUIRED_WHEN: 'REQUIRED_WHEN',
} as const

export const Operators = {
	EQUAL: '==',
	NOT_EQUAL: '!=',
	LESS_THAN_OR_EQUAL: '<=',
	GREATER_THAN_OR_EQUAL: '>=',
	LESS_THAN: '<',
	GREATER_THAN: '>',
	PLUS: '+',
	MINUS: '-',
	MULTIPLY: '*',
	DIVIDE: '/',
	ASSIGN: '=',
} as const

export const LogicalOperators = {
	AND: 'AND',
	OR: 'OR',
} as const

export const Punctuation = {
	OPEN_PAREN: '(',
	CLOSE_PAREN: ')',
	OPEN_BRACE: '{',
	CLOSE_BRACE: '}',
	OPEN_SQUARE_BRACE: '[',
	CLOSE_SQUARE_BRACE: ']',
	COMMA: ',',
	PERIOD: '.',
} as const

export const KeywordArray = _.values(Keywords)
export const ConstantArray = [
	..._.values(Constants),
	..._.keys(CommonResponses),
]
export const LiteralArray = _.values(Literals)
export const FunctionArray = [
	..._.values(CoreFunctions),
	..._.values(ReturnHelpers),
	...helperFunctions.map(f => f.tokenName),
]
export const LogicalOperatorArray = _.values(LogicalOperators)

export const TokenPatterns: TokenPattern[] = [
	{ type: TokenType.WHITESPACE, pattern: /^\s+/ },
	{ type: TokenType.COMMENT, pattern: /^\/\/[^\n]*/ },
	{ type: TokenType.NUMBER, pattern: /^\-*\d+\.*\d*/ },
	{ type: TokenType.STRING, pattern: /^"[^"]*"/ },
	{ type: TokenType.ARRAY, pattern: /^\[.*\]/ },
	{ type: TokenType.LOGICAL_OPERATOR, pattern: new RegExp(`^(${LogicalOperatorArray.join('|')})\\b`) },
	{ type: TokenType.KEYWORD, pattern: new RegExp(`^(${KeywordArray.join('|')})\\b`) },
	{ type: TokenType.LITERAL, pattern: new RegExp(`^(${LiteralArray.join('|')})\\b`) },
	{ type: TokenType.CONSTANT, pattern: new RegExp(`^(${ConstantArray.join('|')})\\b`) },
	{ type: TokenType.FUNCTION, pattern: new RegExp(`^(${FunctionArray.join('|')})\\b`) },
	{ type: TokenType.VALIDATION_KEYWORD, pattern: new RegExp(`^(${_.values(ValidationKeywords).join('|')})\\b`) },
	{ type: TokenType.ACTIVATION_KEYWORD, pattern: new RegExp(`^(${_.values(ActivationKeywords).join('|')})\\b`) },
	{ type: TokenType.IDENTIFIER, pattern: /^[a-zA-Z_]\w*/ },
	{ type: TokenType.OPERATOR, pattern: new RegExp(`^(${Operators.EQUAL}|${Operators.NOT_EQUAL}|${Operators.LESS_THAN_OR_EQUAL}|${Operators.GREATER_THAN_OR_EQUAL}|${Operators.LESS_THAN}|${Operators.GREATER_THAN}|\\${Operators.PLUS}|${Operators.MINUS}|\\${Operators.MULTIPLY}|\\${Operators.DIVIDE}|${Operators.ASSIGN})`) },
	{ type: TokenType.PUNCTUATION, pattern: new RegExp(`^(\\${Punctuation.OPEN_PAREN}|\\${Punctuation.CLOSE_PAREN}|\\${Punctuation.OPEN_BRACE}|\\${Punctuation.CLOSE_BRACE}|\\${Punctuation.OPEN_SQUARE_BRACE}|\\${Punctuation.CLOSE_SQUARE_BRACE}|${Punctuation.COMMA}|${Punctuation.PERIOD})`) },
	{ type: TokenType.LINEBREAK, pattern: /^(\r\n|\n|\r)/ },
]

export const createEOFToken = (lastToken?: Token): ScalarToken => ({
	type: EOF_TOKEN_TYPE,
	value: '',
	line: lastToken?.line ?? 0,
	column: lastToken?.column ?? 0,
})
